import { useNavigate } from 'react-router';
/**
 * The account, in the corner of the header.
 *
 * One control with three states, and the important one is the state that renders nothing.
 *
 * **No sign-in Worker means no account controls at all.** Not a disabled button, not a message:
 * a build with nothing behind `/auth` is an Orbit Mind that works entirely locally, which is what
 * every development build is until `wrangler dev` is running beside it. Offering a button that
 * fails would make a working configuration look broken.
 *
 * **Loading renders nothing too, and only for as long as one request takes.** A skeleton in a
 * header for something that is usually absent — most visitors are signed out, and many
 * deployments have no Worker — costs a layout shift to say "wait" about a thing that may never
 * arrive.
 *
 * **Signed in, the email is the label.** It is the one piece of the account a person recognises,
 * and it is what tells them *which* account this browser is holding, which is the question a
 * second sign-in makes real.
 */
import { useState } from 'react';

import { Button } from '../ui/controls';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../ui/Menu';
import { IconAccount, IconSignOut } from '../ui/icons';

import { FeedbackDialog } from './FeedbackDialog';
import { SignInDialog } from './SignInDialog';
import type { AccountView } from './useAccount';

export interface AccountControlProps {
  readonly state: AccountView;
  readonly onSignOut: (scope: 'this-device' | 'everywhere') => void;
  /** Where a sign-in returns to. The path being looked at now. */
  readonly returnTo: string;
  /** Re-reads the account. Called when an invite code is accepted, which changes what is offered. */
  readonly onRefresh: () => void;
}

export function AccountControl({
  state,
  onSignOut,
  returnTo,
  onRefresh,
}: AccountControlProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  if (
    state.status === 'loading' ||
    state.status === 'storage-error' ||
    state.status === 'unavailable'
  )
    return null;

  if (state.status === 'signed-out') {
    return (
      <>
        <Button
          onClick={() => {
            setDialogOpen(true);
          }}
        >
          <IconAccount size={17} />
          Sign in
        </Button>
        <SignInDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          providers={state.providers}
          returnTo={returnTo}
          inviteNeeded={state.inviteNeeded}
          onInviteAccepted={onRefresh}
          passwordEnabled={state.passwordEnabled === true}
        />
      </>
    );
  }

  return (
    <>
      <Menu
        side="bottom"
        align="end"
        trigger={
          <Button aria-label={`Account — ${state.user.email}`}>
            <IconAccount size={17} />
            <span className="max-w-40 truncate">{state.user.email}</span>
          </Button>
        }
      >
        <MenuItem
          onSelect={() => {
            void navigate('/settings');
          }}
        >
          Settings
        </MenuItem>
        <MenuItem
          onSelect={() => {
            void navigate('/plans');
          }}
        >
          Plans
        </MenuItem>
        <MenuSeparator />
        <MenuLabel>Signed in as</MenuLabel>
        <p className="text-ink-muted truncate px-2.5 pb-2 text-sm">{state.user.displayName}</p>
        <MenuSeparator />
        <MenuItem
          onSelect={() => {
            setFeedbackOpen(true);
          }}
        >
          Send feedback
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          icon={<IconSignOut size={17} />}
          onSelect={() => {
            onSignOut('this-device');
          }}
        >
          Sign out
        </MenuItem>
        <MenuItem
          onSelect={() => {
            onSignOut('everywhere');
          }}
        >
          Sign out on every device
        </MenuItem>
      </Menu>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}
